<template>
  <div v-if="hasFormAccess" class="container-fluid py-4">
    <ToastPrime position="bottom-left" />

    <div class="row g-4">
      <!-- Left Column: Photo & Password -->
      <div class="col-lg-4">
        <!-- Photo Card -->
        <div class="card border-0 shadow-sm mb-4">
          <div class="card-body text-center p-4">

            <div class="d-flex align-items-center mb-3">
              <ButtonPrime
icon="pi pi-arrow-left" text rounded severity="secondary" size="small"
                class="p-0 me-0" style="width: 2rem; height: 2rem;" @click="$router.back()" />
              <h6 class="fw-bold mb-0 text-start">Profile Photo</h6>
            </div>

            <div class="position-relative d-inline-block mb-3">
              <div
                class="avatar-wrapper rounded-circle overflow-hidden bg-light d-flex align-items-center justify-content-center mx-auto"
                style="width: 15rem; height: 15rem; border: 4px solid #fff; box-shadow: 0 4px 12px rgba(0,0,0,0.1);">
                <img
v-if="imagePreview || currentPhoto"
                  :src="imagePreview || (currentPhoto ? `${apiUrl}/${currentPhoto}` : '')" alt="Profile"
                  class="w-100 h-100 object-fit-cover" @error="handleImageError" />
                <i v-else class="pi pi-user text-secondary" style="font-size: 3rem; opacity: 0.3;"></i>
              </div>
              <label
for="photo-upload" class="btn btn-sm btn-primary position-absolute rounded-circle p-2 shadow-sm"
                style="width: 45px; height: 45px; display: flex; align-items: center; justify-content: center; bottom: 13px; right: 15px;">
                <i class="pi pi-camera" style="font-size: 1.5rem;"></i>
              </label>
              <input id="photo-upload" type="file" class="d-none" accept="image/*" @change="onPhotoSelect" />
            </div>

            <p class="text-xs text-muted mb-0">Allowed *.jpeg, *.jpg, *.png</p>
            <p class="text-xs text-muted">Max size of 2 MB</p>
            <small v-if="errorMessage.image" class="text-danger d-block mt-2">{{ errorMessage.image }}</small>
          </div>
        </div>

        <!-- Password Card -->
        <div class="card border-0 shadow-sm">
          <div class="card-body p-4">
            <h6 class="fw-bold mb-3"><i class="pi pi-lock me-2 text-primary"></i>Security</h6>
            <div class="mb-3">
              <label class="form-label text-xs fw-bold text-muted text-uppercase">Password <span
v-if="!isEditMode"
                  class="text-danger">*</span></label>
              <div class="password-input-wrapper">
                <InputText
v-model="manageAccount.password" type="password" 
                  :placeholder="isEditMode ? 'Leave blank to keep current' : '••••••••'"
                  :class="{ 'p-invalid': errorMessage.password }" class="w-100" name="password"
                  :disabled="btnSave.onLoading" toggle-mask @focus="clearError('password')" />
                <button type="button" class="password-toggle-btn" @click="togglePasswordVisibility('password')">
                  <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
                </button>
              </div>
              <small v-if="errorMessage.password" class="text-danger">{{ errorMessage.password }}</small>
              <!-- Password Policy Checklist -->
              <div v-if="manageAccount.password.length > 0" class="mt-2">
                <small class="text-muted d-block mb-1">Password requirements:</small>
                <small :class="passwordPolicy.minLength ? 'text-success' : 'text-danger'" class="d-block">
                  <i :class="passwordPolicy.minLength ? 'fas fa-check' : 'fas fa-times'"></i> Min. 8 characters
                </small>
                <small :class="passwordPolicy.hasUppercase ? 'text-success' : 'text-danger'" class="d-block">
                  <i :class="passwordPolicy.hasUppercase ? 'fas fa-check' : 'fas fa-times'"></i> Uppercase letter (A-Z)
                </small>
                <small :class="passwordPolicy.hasLowercase ? 'text-success' : 'text-danger'" class="d-block">
                  <i :class="passwordPolicy.hasLowercase ? 'fas fa-check' : 'fas fa-times'"></i> Lowercase letter (a-z)
                </small>
                <small :class="passwordPolicy.hasNumber ? 'text-success' : 'text-danger'" class="d-block">
                  <i :class="passwordPolicy.hasNumber ? 'fas fa-check' : 'fas fa-times'"></i> Number (0-9)
                </small>
                <small :class="passwordPolicy.hasSymbol ? 'text-success' : 'text-danger'" class="d-block">
                  <i :class="passwordPolicy.hasSymbol ? 'fas fa-check' : 'fas fa-times'"></i> Symbol (!@#$%^&* etc.)
                </small>
              </div>
            </div>
            <div class="mb-0">
              <label class="form-label text-xs fw-bold text-muted text-uppercase">Confirm Password <span
                  v-if="!isEditMode" class="text-danger">*</span></label>
              <div class="password-input-wrapper">
                <InputText
v-model="manageAccount.password_confirmation" type="password" 
                  :placeholder="isEditMode ? 'Leave blank to keep current' : '••••••••'"
                  :class="{ 'p-invalid': errorMessage.password_confirmation }"
                  class="w-100" name="password_confirmation" :disabled="btnSave.onLoading"
                  @focus="clearError('password_confirmation')" />
                <button
type="button" class="password-toggle-btn"
                  @click="togglePasswordVisibility('password_confirmation')">
                  <i :class="showPasswordConfirmation ? 'pi pi-eye-slash' : 'pi pi-eye'"></i>
                </button>
              </div>
              <small v-if="errorMessage.password_confirmation" class="text-danger">{{
                errorMessage.password_confirmation
              }}</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: User Details -->
      <div class="col-lg-8">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-body p-4">
            <div class="d-flex justify-content-between align-items-center mb-4">
              <h6 class="fw-bold mb-0"><i class="pi pi-id-card me-2 text-primary"></i>Account Information</h6>
              <button
type="button" class="mx-1 mb-0 btn btn-sm btn-primary" :disabled="btnSave.onLoading"
                @click="save">
                <span v-if="!btnSave.onLoading">Save Changes</span>
                <span v-else><i class="pi pi-spin pi-spinner"></i> Saving...</span>
              </button>
            </div>

            <div class="row g-4">
              <!-- Identity Section -->
              <div class="col-12">
                <h6 class="text-uppercase text-secondary text-xs fw-bold mb-2">Identity</h6>
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label fw-semibold">Username <span class="text-danger">*</span></label>
                    <InputText
v-model="manageAccount.username" placeholder="e.g. johndoe"
                      :invalid="!!errorMessage.username" fluid @focus="clearError('username')" />
                    <small v-if="errorMessage.username" class="text-danger">{{ errorMessage.username }}</small>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label fw-semibold">Full Name <span class="text-danger">*</span></label>
                    <InputText
v-model="manageAccount.name" placeholder="Enter full name" :invalid="!!errorMessage.name"
                      fluid @focus="clearError('name')" />
                    <small v-if="errorMessage.name" class="text-danger">{{ errorMessage.name }}</small>
                  </div>
                </div>
              </div>

              <!-- Contact Details Section -->
              <div class="col-12">
                <h6 class="text-uppercase text-secondary text-xs fw-bold mb-2">Contact Details</h6>
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label fw-semibold">Email Address <span class="text-danger">*</span></label>
                    <InputText
v-model="manageAccount.email" type="email" placeholder="name@company.com"
                      :invalid="!!errorMessage.email" fluid @focus="clearError('email')" />
                    <small v-if="errorMessage.email" class="text-danger">{{ errorMessage.email }}</small>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label fw-semibold">Phone Number</label>
                    <InputText
v-model="manageAccount.phone" placeholder="+62..." :invalid="!!errorMessage.phone"
                      fluid @focus="clearError('phone')" @keypress="onPhoneKeypress" />
                    <small v-if="errorMessage.phone" class="text-danger">{{ errorMessage.phone }}</small>
                  </div>
                </div>
              </div>

              <!-- Organization Section -->
              <div class="col-12">
                <h6 class="text-uppercase text-secondary text-xs fw-bold mb-2">Organization</h6>
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label fw-semibold">Company Code <span class="text-danger">*</span></label>
                    <Dropdown
v-model="manageAccount.company_code" :options="companyOptions" option-label="label"
                      option-value="value" placeholder="Select Company" filter
                      :invalid="!!errorMessage.company_code" class="w-100" @focus="clearError('company_code')" />
                    <small v-if="errorMessage.company_code" class="text-danger">{{ errorMessage.company_code
                    }}</small>
                  </div>
                  <!-- <div class="col-md-6">
                    <label class="form-label fw-semibold">Department <span class="text-danger">*</span></label>
                    <AutoComplete
v-model="manageAccount.department" :suggestions="departmentSuggestions"
                      placeholder="Select department" :invalid="!!errorMessage.department"
                      :disabled="!manageAccount.company_code" force-selection
                      input-class="w-100" class="w-100"
                      @complete="searchDepartment" @focus="clearError('department')" />
                    <small v-if="errorMessage.department" class="text-danger">{{ errorMessage.department }}</small>
                  </div> -->
                  <div class="col-md-6">
                    <label for="account-section" class="form-label fw-semibold">Department</label>
                    <MultiSelect id="account-section" v-model="manageAccount.section" :options="sectionOptions" option-label="label"
                      option-value="value" filter placeholder="Search Section (min. 2 characters)" class="w-100" display="chip" show-clear
                      :loading="sectionLoading" @show="loadSectionOptions" @filter="searchSectionOptions" />
                  </div>
                  <div class="col-md-6">
                    <label for="account-sub-section" class="form-label fw-semibold">Section</label>
                    <MultiSelect id="account-sub-section" v-model="manageAccount.sub_section" :options="subSectionOptions" option-label="label"
                      option-value="value" filter placeholder="Search Sub Section (min. 2 characters)" class="w-100" display="chip" show-clear
                      :loading="subSectionLoading" @show="loadSubSectionOptions" @filter="searchSubSectionOptions" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label fw-semibold">Plant</label>
                    <AutoComplete
v-model="manageAccount.plant" :suggestions="plantSuggestions"
                      placeholder="Select plant" :disabled="!manageAccount.company_code"
                      force-selection input-class="w-100" 
                      class="w-100" @complete="searchPlants" />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label fw-semibold">Role</label>
                    <Dropdown
v-model="manageAccount.role_id" :options="roleOptions" option-label="label"
                      option-value="value" placeholder="Select role" filter
                      :invalid="!!errorMessage.role_id" class="w-100"
                      show-clear @focus="clearError('role_id')" />
                    <small v-if="errorMessage.role_id" class="text-danger">{{ errorMessage.role_id }}</small>
                  </div>
                  <div v-if="false" class="col-md-6">
                    <label class="form-label fw-semibold">Catalog Type</label>
                    <AutoComplete
v-model="manageAccount.catalog_type" :suggestions="catalogTypeSuggestions"
                      placeholder="Enter catalog type" :disabled="!manageAccount.company_code"
                      force-selection="false" input-class="w-100" 
                      class="w-100" @complete="searchCatalogTypes" />
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
  <NoAccess v-else />
</template>

<script>
import { mapState } from "vuex";
import api from '@/helpers/api';
import baseApiUrl from '@/configs/config';
import manageAccountService from '@/services/manageAccount/manageAccountService';
import companiesService from '@/services/generalMasterData/companiesService';
import manageRoleService from '@/services/manageAccount/manageRoleService';
import generalMasterDataService from '@/services/generalMasterData/generalMasterDataService';
import catalogTypesService from '@/services/generalMasterData/catalogTypesService';
import setMaxSize from "@/helpers/setMaxSize";
import NoAccess from "@/components/NoAccess.vue";

export default {
  name: 'ManageAccountCreateAndEdit',
  components: { NoAccess },
  beforeRouteUpdate(to, from, next) {
    if (this.hasFormAccess) {
      this.loadData();
    }
    next();
  },
  data() {
    return {
      btnSave: {
        onLoading: false,
      },
      manageAccount: {
        username: '',
        name: '',
        email: '',
        phone: '',
        company_code: '',
        department: '',
        section: [],
        sub_section: [],
        catalog_type: '',
        plant: '',
        status: 'active',
        role_id: null,
        password: '',
        password_confirmation: ''
      },
      imageFile: null,
      currentPhoto: null,
      apiUrl: baseApiUrl.storageUrl,
      errorMessage: {},
      statusOptions: [
        { label: 'Active', value: 'active' },
        { label: 'Inactive', value: 'inactive' }
      ],
      showPassword: false,
      showPasswordConfirmation: false,
      companyOptions: [],
      roleOptions: [],
      departmentSuggestions: [],
      sectionOptions: [],
      subSectionOptions: [],
      allSectionOptions: [],
      allSubSectionOptions: [],
      sectionLoading: false,
      subSectionLoading: false,
      catalogTypeSuggestions: [],
      plantSuggestions: [],
      imagePreview: null,
      maxValidation: {
        size: 2000000,
        text: "Max Size 2000 KB"
      },
    }
  },
  computed: {
    ...mapState({
      authUserPermissions: (state) => state.auth.userHasPermissions || [],
    }),
    hasFormAccess() {
      return this.isEditMode
        ? this.hasAnyPermission('user-update')
        : this.hasAnyPermission('user-create');
    },
    isEditMode() {
      return this.$route.params.id || this.$route.params.dataUserCode;
    },
    editId() {
      return this.$route.params.id || this.$route.params.dataUserCode;
    },
    passwordPolicy() {
      const p = this.manageAccount.password;
      return {
        minLength: p.length >= 8,
        hasUppercase: /[A-Z]/.test(p),
        hasLowercase: /[a-z]/.test(p),
        hasNumber: /[0-9]/.test(p),
        hasSymbol: /[^A-Za-z0-9]/.test(p),
      };
    },
    isPasswordPolicyValid() {
      const pol = this.passwordPolicy;
      return pol.minLength && pol.hasUppercase && pol.hasLowercase && pol.hasNumber && pol.hasSymbol;
    },
    clientCode() {
      const userAuth = JSON.parse(localStorage.getItem('userAuth') || '{}');
      return userAuth?.user?.client_code || userAuth?.user?.company_code || '';
    },
  },
  watch: {
    '$route.params.id'(newVal, oldVal) {
      if (newVal !== oldVal) this.loadData();
    },
    '$route.params.dataUserCode'(newVal, oldVal) {
      if (newVal !== oldVal) this.loadData();
    },
  },
  mounted() {
    if (this.hasFormAccess) {
      this.loadData();
      this.loadCompanies();
      this.loadRoles();
      this.getMaxSizeConfig();
    }
  },
  methods: {
    hasAnyPermission(...perms) {
      return perms.some((p) => this.authUserPermissions.includes(p));
    },
    // Extract code from "CODE - Description" format
    extractCode(value) {
      if (!value) return '';
      // If format is "CODE - Description", extract CODE
      if (value.includes(' - ')) {
        return value.split(' - ')[0].trim();
      }
      return value;
    },
    
    async loadData() {
      if (this.isEditMode && this.editId) {
        await this.loadEditData();
      } else {
        this.resetForm();
      }
    },

    async loadEditData() {
      try {
        const apiResponse = await api(this, manageAccountService.get(this.editId), false);
        const userData = apiResponse.data;

        // Fetch plant detail if exists
        let plantDisplay = '';
        if (userData.plants && userData.plants.length > 0) {
          const plantCode = userData.plants[0];
          try {
            const plantRes = await api(this, generalMasterDataService.list('plant'), false);
            const plant = (plantRes.data || []).find(p => p.code === plantCode);
            plantDisplay = plant ? `${plant.code} - ${plant.description}` : plantCode;
          } catch {
            plantDisplay = plantCode;
          }
        }

        // Fetch catalog type detail if exists
        let catalogTypeDisplay = '';
        if (userData.catalog_types && userData.catalog_types.length > 0) {
          const catalogCode = userData.catalog_types[0];
          try {
            const catalogRes = await api(this, catalogTypesService.list({ 
              company_code: userData.company_code,
              limit: 0 
            }), false);
            const catalog = (catalogRes.data || []).find(c => c.code === catalogCode);
            catalogTypeDisplay = catalog ? `${catalog.code} - ${catalog.description}` : catalogCode;
          } catch {
            catalogTypeDisplay = catalogCode;
          }
        }

        // Fetch department detail if exists
        let departmentDisplay = '';
        if (userData.department) {
          try {
            const deptRes = await api(this, generalMasterDataService.list('department', { 
              company_code: userData.company_code,
              limit: 0 
            }), false);
            const dept = (deptRes.data || []).find(d => d.code === userData.department);
            departmentDisplay = dept ? `${dept.code} - ${dept.description}` : userData.department;
          } catch {
            departmentDisplay = userData.department;
          }
        }

        // Section/sub-section bisa lebih dari satu (relasi user_sections/user_sub_sections);
        // fallback ke kolom single `section` untuk data lama.
        const sections = Array.isArray(userData.sections) && userData.sections.length
          ? userData.sections.filter(Boolean)
          : (userData.section ? [userData.section] : []);
        const subSections = Array.isArray(userData.sub_sections) ? userData.sub_sections.filter(Boolean) : [];

        this.manageAccount = {
          id: userData.id,
          username: userData.username || '',
          name: userData.name || '',
          namecode: userData.namecode || '',
          email: userData.email || '',
          phone: userData.phone || '',
          company_code: userData.company_code || '',
          department: departmentDisplay,
          section: [...sections],
          sub_section: [...subSections],
          catalog_type: catalogTypeDisplay,
          plant: plantDisplay,
          status: userData.status || 'active',
          role_id: userData.roles && userData.roles.length > 0 ? userData.roles[0].id : null,
          password: '',
          password_confirmation: ''
        };

        this.currentPhoto = userData.image || null;

        // Populate suggestions
        if (departmentDisplay) {
          this.departmentSuggestions = [departmentDisplay];
        }
        // Tampilkan nilai terpilih tanpa memanggil API (API baru dipanggil saat dropdown dibuka).
        this.manageAccount.section.forEach((v) => this.ensureSelectedOption(this.sectionOptions, v));
        this.manageAccount.sub_section.forEach((v) => this.ensureSelectedOption(this.subSectionOptions, v));

      } catch (error) {
        console.error('Error loading edit data:', error);
        if (error.response?.data?.code === 400) {
          error.response.data.data.forEach((v) => {
            this.errorMessage[v.path] = v.msg;
          });
        }
      }
    },

    resetForm() {
      this.manageAccount = {
        username: '',
        name: '',
        email: '',
        phone: '',
        company_code: null,
        department: '',
        section: [],
        sub_section: [],
        catalog_type: '',
        plant: '',
        status: 'active',
        role_id: null,
        password: '',
        password_confirmation: ''
      };
      this.imageFile = null;
      this.currentPhoto = null;
      this.imagePreview = null;
      this.errorMessage = {};
    },

    clearError(field) {
      delete this.errorMessage[field];
    },

    togglePasswordVisibility(field) {
      if (field === 'password') {
        this.showPassword = !this.showPassword;
        this.$nextTick(() => {
          const input = this.$el.querySelector('input[name="password"]');
          if (input) {
            input.type = this.showPassword ? 'text' : 'password';
          }
        });
      } else if (field === 'password_confirmation') {
        this.showPasswordConfirmation = !this.showPasswordConfirmation;
        this.$nextTick(() => {
          const input = this.$el.querySelector('input[name="password_confirmation"]');
          if (input) {
            input.type = this.showPasswordConfirmation ? 'text' : 'password';
          }
        });
      }
    },

    async searchCompany(event) {
      const query = event.query;
      if (!query || query.length < 2) {
        this.companyOptions = [];
        return;
      }
      try {
        const res = await api(this, companiesService.list({ search: query, limit: 10 }), false);
        this.companyOptions = (res.data.data || []).map(c => ({
          label: c.label,
          value: c.code
        }));
      } catch {
        this.companyOptions = [];
      }
    },

    async loadCompanies() {
      try {
        const res = await api(this, companiesService.list({ limit: 0 }), false);
        this.companyOptions = (res.data || []).map(c => ({
          label: `${c.code} - ${c.description}`,
          value: c.code
        }));
      } catch (error) {
        console.error('Error loading companies:', error);
        this.companyOptions = [];
      }
    },

    async loadRoles() {
      try {
        const res = await api(this, manageRoleService.list({ limit: 0 }), false);
        this.roleOptions = (res.data || []).map(r => ({
          label: r.name,
          value: r.id
        }));
      } catch (error) {
        console.error('Error loading roles:', error);
        this.roleOptions = [];
      }
    },

    onPhoneInput(event) {
      // Only allow numbers
      this.manageAccount.phone = event.target.value.replace(/[^0-9]/g, '');
    },

    onPhoneKeypress(event) {
      // Prevent non-numeric characters
      const charCode = event.which ? event.which : event.keyCode;
      if (charCode < 48 || charCode > 57) {
        event.preventDefault();
      }
    },

    async searchDepartment(event) {
      const query = event.query;

      if (!this.manageAccount.company_code) {
        this.departmentSuggestions = [];
        return;
      }

      try {
        const res = await api(this, generalMasterDataService.list('department', { 
          company_code: this.manageAccount.company_code,
          limit: 0 
        }), false);
        const departments = (res.data || [])
          .map(d => `${d.code} - ${d.description}`)
          .filter(label => label.toLowerCase().includes(query.toLowerCase()));
        this.departmentSuggestions = departments;
      } catch (error) {
        console.error('Error loading departments:', error);
        this.departmentSuggestions = [];
      }
    },

    mapOptions(list, query = '') {
      const options = new Map();
      (Array.isArray(list) ? list : []).forEach((item) => {
        const code = item?.code || '';
        const label = item?.description || code;
        if (code && !options.has(code)) options.set(code, { label, value: code });
      });
      let result = [...options.values()];
      if (query) result = this.filterOptionsByQuery(result, query);
      return result;
    },
    filterOptionsByQuery(options, query) {
      const q = query.toLowerCase();
      return options.filter(o => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
    },
    // Pastikan nilai terpilih tetap tampil walau tidak ketemu di opsi (mis. data edit).
    ensureSelectedOption(options, value) {
      if (!value) return;
      if (options.some(o => o.value === value)) return;
      options.unshift({ label: value, value });
    },
    async fetchSectionOptions(search, limit) {
      this.sectionLoading = true;
      try {
        const params = { limit, client: this.clientCode };
        if (search) params.search = search;
        const res = await api(this, generalMasterDataService.list('section', params), false);
        return this.mapOptions(res?.data?.data || res?.data || [], search);
      } catch {
        return [];
      } finally {
        this.sectionLoading = false;
      }
    },
    async fetchSubSectionOptions(search, limit) {
      this.subSectionLoading = true;
      try {
        const params = { limit, client: this.clientCode };
        if (search) params.search = search;
        const res = await api(this, generalMasterDataService.list('sub-section', params), false);
        return this.mapOptions(res?.data?.data || res?.data || [], search);
      } catch {
        return [];
      } finally {
        this.subSectionLoading = false;
      }
    },
    async loadSectionOptions() {
      const fetched = await this.fetchSectionOptions('', 10);
      this.sectionOptions = Array.isArray(fetched) ? fetched : [];
      this.allSectionOptions = [...this.sectionOptions];
      if (!Array.isArray(this.manageAccount.section)) this.manageAccount.section = [];
      this.manageAccount.section.forEach((v) => this.ensureSelectedOption(this.sectionOptions, v));
    },
    async loadSubSectionOptions() {
      const fetched = await this.fetchSubSectionOptions('', 10);
      this.subSectionOptions = Array.isArray(fetched) ? fetched : [];
      this.allSubSectionOptions = [...this.subSectionOptions];
      if (!Array.isArray(this.manageAccount.sub_section)) this.manageAccount.sub_section = [];
      this.manageAccount.sub_section.forEach((v) => this.ensureSelectedOption(this.subSectionOptions, v));
    },
    // Search server-side: hit API begitu ketik min. 2 karakter.
    // Opsi terpilih (bisa lebih dari satu) wajib dipertahankan agar chip MultiSelect tidak hilang.
    async searchSectionOptions(event) {
      const query = (event?.value || '').trim();
      const selectedValues = this.manageAccount.section || [];
      if (query.length < 2) {
        const base = [...this.allSectionOptions];
        selectedValues.forEach((v) => this.ensureSelectedOption(base, v));
        this.sectionOptions = base;
        return;
      }
      let options = await this.fetchSectionOptions(query, 10);
      // Kalau backend abaikan param `search` / balik kosong, saring daftar yang sudah dimuat.
      if (!options.length) options = this.filterOptionsByQuery(this.allSectionOptions, query);
      const optionValues = new Set(options.map((o) => o.value));
      const missingSelected = this.sectionOptions.filter((o) => selectedValues.includes(o.value) && !optionValues.has(o.value));
      this.sectionOptions = [...missingSelected, ...options];
    },
    async searchSubSectionOptions(event) {
      const query = (event?.value || '').trim();
      const selectedValues = this.manageAccount.sub_section || [];
      if (query.length < 2) {
        const base = [...this.allSubSectionOptions];
        selectedValues.forEach((v) => this.ensureSelectedOption(base, v));
        this.subSectionOptions = base;
        return;
      }
      let options = await this.fetchSubSectionOptions(query, 10);
      if (!options.length) options = this.filterOptionsByQuery(this.allSubSectionOptions, query);
      const optionValues = new Set(options.map((o) => o.value));
      const missingSelected = this.subSectionOptions.filter((o) => selectedValues.includes(o.value) && !optionValues.has(o.value));
      this.subSectionOptions = [...missingSelected, ...options];
    },
    // FormData tidak bisa kirim array kosong; kirim key kosong supaya backend tahu isinya dikosongkan.
    appendArrayField(formData, key, values) {
      const list = (Array.isArray(values) ? values : []).filter(Boolean);
      if (!list.length) {
        formData.append(key, '');
        return;
      }
      list.forEach((v) => formData.append(`${key}[]`, v));
    },

    async searchCatalogTypes(event) {
      const query = event.query;
      
      if (!this.manageAccount.company_code) {
        this.catalogTypeSuggestions = [];
        return;
      }
      
      try {
        const res = await api(this, catalogTypesService.list({ 
          company_code: this.manageAccount.company_code,
          limit: 0 
        }), false);
        const catalogTypes = (res.data || [])
          .map(ct => `${ct.code} - ${ct.description}`)
          .filter(label => label.toLowerCase().includes(query.toLowerCase()));
        this.catalogTypeSuggestions = catalogTypes.length > 0 ? catalogTypes : (query ? [query] : []);
      } catch (error) {
        this.catalogTypeSuggestions = query ? [query] : [];
      }
    },

    async searchPlants(event) {
      const query = event.query;
      
      if (!this.manageAccount.company_code) {
        this.plantSuggestions = [];
        return;
      }
      
      try {
        const res = await api(this, generalMasterDataService.list('plant'), false);
        const plants = (res.data || [])
          .map(p => `${p.code} - ${p.description}`)
          .filter(label => label.toLowerCase().includes(query.toLowerCase()));
        this.plantSuggestions = plants;
      } catch (error) {
        this.plantSuggestions = [];
      }
    },

    onPhotoSelect(event) {
      const file = event.target.files[0];

      if (file && !['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
        this.errorMessage.image = 'Only PNG and JPEG files are allowed';
        this.imageFile = null;
        this.imagePreview = null;
        return false;
      }

      if (file.size > this.maxValidation.size) {
        this.errorMessage.image = `Max Size ${this.maxValidation.size} KB`;
        this.imageFile = null;
        this.imagePreview = null;
        return false;
      }

      this.imageFile = file;
      this.clearError('image');

      // Create preview
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.imagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },

    getCurrentPhotoName() {
      if (!this.currentPhoto) return '';
      return this.currentPhoto.substring(this.currentPhoto.lastIndexOf('-') + 1);
    },

    handleImageError(event) {
      // Hide broken image and show placeholder or error message
      event.target.style.display = 'none';
    },

    validateForm() {
      this.errorMessage = {};
      let isValid = true;

      const requiredFields = [
        { key: 'username', message: 'Username is Required' },
        { key: 'name', message: 'Name is Required' },
        { key: 'email', message: 'Email is Required', isEmail: true },
        { key: 'company_code', message: 'Company is Required' },
        // { key: 'department', message: 'Department is Required' }
      ];

      // Validate required fields
      for (const field of requiredFields) {
        let value = this.manageAccount[field.key];

        if (!value || (typeof value === 'string' && value.trim() === '')) {
          this.errorMessage[field.key] = field.message;
          isValid = false;
        }

        if (field.isEmail && value) {
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailPattern.test(value)) {
            this.errorMessage[field.key] = 'Email must be valid';
            isValid = false;
          }
        }
      }

      // Validate password for create mode or when password is filled in edit mode
      if (!this.isEditMode || this.manageAccount.password) {
        if (!this.manageAccount.password) {
          this.errorMessage.password = 'Password is Required';
          isValid = false;
        } else if (!this.isPasswordPolicyValid) {
          this.errorMessage.password = 'Password does not meet all requirements';
          isValid = false;
        }

        if (!this.manageAccount.password_confirmation) {
          this.errorMessage.password_confirmation = 'Password Confirmation is Required';
          isValid = false;
        }

        if (this.manageAccount.password && this.manageAccount.password !== this.manageAccount.password_confirmation) {
          this.errorMessage.password_confirmation = 'Password Confirmation must match Password';
          isValid = false;
        }
      }

      return isValid;
    },

    async save() {
      if (!this.validateForm()) {
        return;
      }

      this.btnSave.onLoading = true;

      try {
        if (this.isEditMode) {
          // Update existing account
          const formData = new FormData();
          formData.append('_method', 'PUT');
          formData.append('username', this.manageAccount.username);
          formData.append('name', this.manageAccount.name);
          formData.append('email', this.manageAccount.email);
          formData.append('phone', this.manageAccount.phone || '');
          formData.append('company_code', this.manageAccount.company_code);
          formData.append('department', this.extractCode(this.manageAccount.department) || '');
          this.appendArrayField(formData, 'sections', this.manageAccount.section);
          this.appendArrayField(formData, 'sub_sections', this.manageAccount.sub_section);
          formData.append('catalog_type', this.extractCode(this.manageAccount.catalog_type) || '');
          formData.append('plant', this.extractCode(this.manageAccount.plant) || '');
          formData.append('status', this.manageAccount.status);

          if (this.manageAccount.role_id) {
            formData.append('role_id', this.manageAccount.role_id);
          }

          // Only append image if there's a new image file
          if (this.imageFile) {
            formData.append('image', this.imageFile);
          }

          if (this.manageAccount.password) {
            formData.append('password', this.manageAccount.password);
            formData.append('password_confirmation', this.manageAccount.password_confirmation);
          }

          await api(this, manageAccountService.updateWithFile(this.editId, formData));
        } else {
          // Create new account
          const formData = new FormData();
          formData.append('username', this.manageAccount.username);
          formData.append('name', this.manageAccount.name);
          formData.append('email', this.manageAccount.email);
          formData.append('phone', this.manageAccount.phone || '');
          formData.append('company_code', this.manageAccount.company_code);
          formData.append('department', this.extractCode(this.manageAccount.department) || '');
          this.appendArrayField(formData, 'sections', this.manageAccount.section);
          this.appendArrayField(formData, 'sub_sections', this.manageAccount.sub_section);
          formData.append('catalog_type', this.extractCode(this.manageAccount.catalog_type) || '');
          formData.append('plant', this.extractCode(this.manageAccount.plant) || '');
          formData.append('password', this.manageAccount.password);
          formData.append('password_confirmation', this.manageAccount.password_confirmation);

          if (this.manageAccount.role_id) {
            formData.append('role_id', this.manageAccount.role_id);
          }

          if (this.imageFile) {
            formData.append('image', this.imageFile);
          }

          await api(this, manageAccountService.create(formData));
        }

        // Show success toast and redirect after a short delay
        // this.$toastNotifPrime('success', 'Success', this.isEditMode ? 'Account updated successfully' : 'Account created successfully');

        // Redirect after toast appears
        setTimeout(() => {
          this.$router.back();
        }, 1000);

      } catch (error) {
        console.error('Error saving account:', error);

        // Handle validation errors (422)
        if (error.response?.status === 422) {
          const errors = error.response.data.errors;
          if (errors) {
            Object.keys(errors).forEach(key => {
              this.errorMessage[key] = errors[key][0]; // Ambil error pertama
            });
          }
        }
        // Handle custom validation (400)
        else if (error.response?.data?.code === 400) {
          error.response.data.data.forEach((v) => {
            this.errorMessage[v.path] = v.msg;
          });
        }
        // Handle other errors - NEVER show stack trace
        else {
          const errorMessage = error.response?.data?.message ||
            error.message ||
            'Failed to save account. Please try again.';

          this.$toast.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
            life: 5000
          });
        }
      } finally {
        this.btnSave.onLoading = false;
      }
    },
    async getMaxSizeConfig() {
      try {
        const result = await setMaxSize();
        if (result) {
          this.maxValidation.size = result.data.data.maximum_file;
          this.maxValidation.text = `Max Size ${result.data.data.maximum_file / 1000} KB`;
        }
      } catch (error) {
        this.maxValidation.size = 2000000;
        this.maxValidation.text = `Max Size ${this.maxValidation.size / 1000} KB`;
      } 
    },
  }
};
</script>

<style scoped>
.form-label {
  font-weight: 500;
  font-size: 0.875rem;
  color: #344767;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #344767;
}

.text-sm {
  font-size: 0.875rem;
}

.password-input-wrapper {
  position: relative;
  width: 100%;
}

.password-input-wrapper .p-inputtext {
  width: 100%;
  padding-right: 2.5rem;
}

.password-toggle-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.password-toggle-btn:focus {
  outline: none;
}

.avatar-wrapper {
  position: relative;
  transition: all 0.3s ease;
}

.avatar-wrapper:hover {
  transform: scale(1.02);
}

.text-xs {
  font-size: 0.75rem;
}

.p-card {
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.05) !important;
}
</style>